import { Table, Thead, Tbody, Tr, Th, Td } from '@components/core-ui/Table/Table';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { cn } from '@helpers/cn';
import type { ReactNode } from 'react';
import { formatDurationMs } from '@pages/Admin/AsrProxyConsolePage/helpers/formatDurationMs';
import { formatRetryPolicy } from '@pages/Admin/AsrProxyConsolePage/helpers/formatRetryPolicy';
import { getModelSelectionSourceLabel } from '@pages/Admin/AsrProxyConsolePage/helpers/getModelSelectionSourceLabel';
import { getStageOffsetsMs } from '@pages/Admin/AsrProxyConsolePage/helpers/getStageOffsetsMs';
import { STEP_TITLES } from '../../constants';
import type { TProps } from './types/TProps';

const Block = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="rounded-md border border-border bg-page p-3">
    <h4 className="text-[11px] font-semibold uppercase tracking-wide text-foreground-muted">{title}</h4>
    <div className="mt-2 text-xs text-foreground">{children}</div>
  </section>
);

const Muted = ({ children }: { children: ReactNode }) => <p className="text-xs text-foreground-muted">{children}</p>;

const Json = ({ value }: { value: unknown }) => (
  <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded border border-border bg-surface p-2 text-[11px] leading-4">
    {JSON.stringify(value, null, 2)}
  </pre>
);

const UtteranceList = ({ utterances }: { utterances: { speaker: string; text: string }[] }) => (
  <ul className="grid gap-1.5">
    {utterances.map((utterance, index) => (
      <li key={`${index}-${utterance.speaker}`} className="flex gap-2">
        <span className="shrink-0 font-semibold text-accent">{utterance.speaker}</span>
        <span>{utterance.text}</span>
      </li>
    ))}
  </ul>
);

export const RouteStepDetails = ({
  phase,
  failedPhase,
  jobId,
  execution,
  audioUrl,
  outcome,
  transcript,
  errorMessage,
  onClose,
}: TProps) => {
  const { plan, stages, attempts } = execution;
  const lastAttempt = attempts.at(-1);
  const preview = execution.mappedTranscriptPreview;
  const changes = execution.normalizerChanges ?? [];
  const resultReady = outcome !== 'awaiting-response';

  const renderContent = () => {
    switch (phase) {
      case 0:
        return (
          <Block title="Request">
            <Json
              value={{
                jobId,
                audioUrl,
                model: plan.model,
                modelSelectedBy: getModelSelectionSourceLabel(plan.modelSelectionSource),
                mode: plan.synchronous ? 'synchronous' : 'async',
              }}
            />
          </Block>
        );
      case 1:
        return (
          <Block title="Job">
            <Muted>
              {plan.synchronous
                ? 'Persisted and executed in the same request.'
                : 'Persisted; the durable workflow picks the job up and the console polls for the result.'}
            </Muted>
          </Block>
        );
      case 2:
        return (
          <div className="grid gap-3 md:grid-cols-2">
            <Block title="Pipeline configured for this request">
              <Json value={{ normalizers: plan.normalizers, qualityCheckers: plan.qualityCheckers }} />
            </Block>
            <Block title="Recovery policy">
              <Muted>{formatRetryPolicy(plan.retryPolicy)}</Muted>
              <Muted>
                Fallback models: {plan.fallbackModels.length > 0 ? plan.fallbackModels.join(', ') : 'none configured'}
              </Muted>
            </Block>
          </div>
        );
      case 3:
        return (
          <div className="grid gap-3 md:grid-cols-2">
            <Block title="Usable provider options">
              <Json value={plan.options} />
            </Block>
            <Block title="Ignored options">
              {plan.ignoredOptions.length > 0 ? <Json value={plan.ignoredOptions} /> : <Muted>None</Muted>}
            </Block>
          </div>
        );
      case 4:
      case 5:
        return (
          <Block title="Provider attempt">
            {lastAttempt ? <Json value={lastAttempt} /> : <Muted>No attempt recorded yet.</Muted>}
          </Block>
        );
      case 6:
        return (
          <Block title="AssemblyAI-first transcript before normalization">
            {preview ? (
              <>
                <Muted>
                  {preview.utteranceCount} utterances · {preview.characterCount} characters · showing the first{' '}
                  {preview.utterances.length}
                </Muted>
                <div className="mt-2">
                  <UtteranceList utterances={preview.utterances} />
                </div>
              </>
            ) : (
              <Muted>
                {resultReady ? 'No preview was recorded for this job.' : 'Available once the provider responds.'}
              </Muted>
            )}
          </Block>
        );
      case 7:
        return (
          <div className="grid gap-3">
            {changes.length === 0 ? (
              <Block title="Normalizer changes">
                <Muted>
                  {resultReady
                    ? 'No normalizer changes were recorded for this job.'
                    : 'Available once the provider responds.'}
                </Muted>
              </Block>
            ) : (
              changes.map((change) => (
                <Block key={change.name} title={change.name}>
                  <Muted>
                    {change.changedUtteranceCount} of {change.utteranceCountBefore} utterances changed
                    {change.utteranceCountAfter !== change.utteranceCountBefore
                      ? ` · ${change.utteranceCountAfter} after`
                      : ''}
                    {change.truncated ? ` · showing the first ${change.changedUtterances.length}` : ''}
                  </Muted>
                  {change.changedUtterances.length > 0 ? (
                    <Table className="mt-2 w-full table-fixed text-[11px]">
                      <Thead className="text-left text-foreground-muted">
                        <Tr>
                          <Th className="w-10 pb-1 font-medium">#</Th>
                          <Th className="pb-1 font-medium">Before</Th>
                          <Th data-mobile-summary className="pb-1 font-medium">
                            After
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {change.changedUtterances.map((utterance) => (
                          <Tr key={utterance.index} className="border-t border-dashed border-border align-top">
                            <Td className="py-1 pr-2 text-foreground-muted">
                              {utterance.index + 1}
                              <span className="block font-semibold text-accent">{utterance.speaker}</span>
                            </Td>
                            <Td className="whitespace-normal break-words py-1 pr-2 text-danger line-through decoration-danger/60">
                              {utterance.before}
                            </Td>
                            <Td className="whitespace-normal break-words py-1 text-success">{utterance.after}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  ) : null}
                </Block>
              ))
            )}
          </div>
        );
      case 8:
        return (
          <Block title="Quality checks">
            {transcript?.quality_checks?.length ? (
              <Json value={transcript.quality_checks} />
            ) : (
              <Muted>
                {resultReady ? 'No quality checks ran for this job.' : 'Available once the provider responds.'}
              </Muted>
            )}
          </Block>
        );
      default:
        return (
          <Block title="Result">
            <Json
              value={{
                jobId,
                status: transcript?.status ?? outcome,
                attempts: attempts.length,
                retriesUsed: lastAttempt?.transientErrors ?? 0,
                fallbacksApplied: execution.fallbacksApplied,
                normalizersApplied: execution.normalizersApplied ?? [],
              }}
            />
          </Block>
        );
    }
  };

  return (
    <aside
      aria-label={`Step details: ${STEP_TITLES[phase] ?? 'Step'}`}
      className="mt-4 flex min-h-0 flex-col rounded-lg border border-accent bg-surface p-4 shadow-[0_8px_20px_var(--color-shadow)]"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-foreground">
          Step {phase + 1} · {STEP_TITLES[phase] ?? 'Step'}
        </h3>
        <OutlinedButton className="!w-auto" onClick={onClose}>
          Close details
        </OutlinedButton>
      </div>
      <div className="grid min-h-0 gap-3 overflow-auto lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="grid min-w-0 gap-3">
          {failedPhase === phase && errorMessage ? (
            <Block title="Recorded error at this step">
              <p role="alert" className="whitespace-pre-wrap break-words text-danger">
                {errorMessage}
              </p>
            </Block>
          ) : null}
          {renderContent()}
        </div>
        <Block title="Recorded stages">
          {stages.length === 0 ? (
            <Muted>No stages recorded yet.</Muted>
          ) : (
            <ol className="grid gap-1">
              {getStageOffsetsMs(stages).map(({ stage, offsetMs }, index) => (
                <li key={`${stage.name}-${index}`} className={cn('flex items-baseline justify-between gap-3')}>
                  <span className="font-mono text-[11px]">{stage.name}</span>
                  <span className="shrink-0 tabular-nums text-[11px] text-foreground-muted">
                    +{formatDurationMs(offsetMs)}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </Block>
      </div>
    </aside>
  );
};
